/**
 * src/pages/ProductsPage.js — Premium browsing with animated filters and staggered grid
 */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { productsApi, unwrapPage } from '../services/api';
import ProductCard from '../components/common/ProductCard';
import { ErrorBox, ProductCardSkeleton } from '../components/common/ErrorBoundary';
import { EmptyState } from '../components/ui';

const CATS = [
  { slug:'', name:'All', icon:'🎆' },
  { slug:'one-sound', name:'One Sound Crackers', icon:'💥' },
  { slug:'wala-items', name:'Wala Items', icon:'🎵' },
  { slug:'bomb', name:'Bomb', icon:'💣' },
  { slug:'flower-pots', name:'Flower Pots', icon:'🌸' },
  { slug:'ground', name:'Ground Chakkar', icon:'🌀' },
  { slug:'candles', name:'Candles', icon:'🕯️' },
  { slug:'rockets', name:'Rockets', icon:'🚀' },
  { slug:'fountains', name:'Fountains', icon:'⛲' },
  { slug:'showers', name:'Showers', icon:'🌟' },
  { slug:'premium-products', name:'Premium Products', icon:'👑' },
  { slug:'sky-shots', name:'Sky Shots', icon:'🎆' },
  { slug:'repeating-skyshots', name:'Repeating Skyshots', icon:'🎇' },
  { slug:'sparklers', name:'Sparklers', icon:'✨' },
  { slug:'matches', name:'Matches', icon:'🔥' },
  { slug:'gift-boxes', name:'Gift Boxes', icon:'🎁' },
  { slug:'combo-packs',  name:'Combo Items',  icon:'🎊' },
  { slug:'kids-special', name:'Kids Special', icon:'🎈' },
];

const PRICE_RANGES = [
  { label:'Under ₹200', min:'',    max:'200'  },
  { label:'₹200–₹500',  min:'200', max:'500'  },
  { label:'₹500–₹1000', min:'500', max:'1000' },
  { label:'₹1000+',     min:'1000',max:''     },
];

const SORT_OPTIONS = [
  { value:'-created_at', label:'Newest First'      },
  { value:'price',       label:'Price: Low → High' },
  { value:'-price',      label:'Price: High → Low' },
  { value:'name',        label:'Name A–Z'          },
  { value:'-is_featured',label:'Featured First'    },
];

// FilterPanel extracted outside component so it never remounts on parent re-render
function FilterPanel({ search, setSearch, category, setCategory,
                       minPrice, setMinPrice, maxPrice, setMaxPrice,
                       inStock, setInStock, reset, onClose }) {
  return (

<div className="space-y-6">
  {/* Search */}
  <div>
    <p className="text-[10px] font-black text-gray-400 uppercase tracking-[0.15em] mb-2.5">Search</p>
    <div className="relative">
      <input value={search} onChange={e=>setSearch(e.target.value)} placeholder="Sparklers, rockets…"
        className="w-full bg-gray-50 border border-gray-200 rounded-xl py-2.5 pl-9 pr-3 text-sm
          outline-none focus:bg-white focus:ring-2 focus:ring-yellow-400/30 focus:border-yellow-500 transition-all"/>
      <svg className="absolute left-3 top-3 w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-4.35-4.35M17 11A6 6 0 115 11a6 6 0 0112 0z"/>
      </svg>
    </div>
  </div>

  {/* Categories */}
  <div>
    <p className="text-[10px] font-black text-gray-400 uppercase tracking-[0.15em] mb-2.5">Category</p>
    <div className="space-y-0.5 max-h-[420px] overflow-y-auto pr-1 scrollbar-thin">
      {CATS.map(c => (
        <button key={c.slug} onClick={() => { setCategory(c.slug); onClose?.(); }}
          className={`w-full text-left text-sm px-3.5 py-2.5 rounded-xl flex items-center gap-2.5 font-semibold transition-all duration-200
            ${category===c.slug
              ? 'bg-gradient-to-r from-red-600 to-red-700 text-white shadow-md shadow-red-600/25'
              : 'text-gray-600 hover:bg-red-50 hover:text-red-700'}`}>
          <span className="text-base w-5 text-center">{c.icon}</span>
          {c.name}
          {category===c.slug && (
            <svg className="ml-auto w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7"/>
            </svg>
          )}
        </button>
      ))}
    </div>
  </div>

  {/* Price range */}
  <div>
    <p className="text-[10px] font-black text-gray-400 uppercase tracking-[0.15em] mb-2.5">Price Range</p>
    <div className="flex gap-2 mb-2.5">
      <div className="relative flex-1">
        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-xs text-gray-400 font-bold">₹</span>
        <input type="number" value={minPrice} onChange={e=>setMinPrice(e.target.value)} placeholder="Min" min="0"
          className="w-full bg-gray-50 border border-gray-200 rounded-xl py-2.5 pl-7 pr-2 text-sm outline-none focus:ring-2 focus:ring-yellow-400/30 focus:border-yellow-500 focus:bg-white transition-all"/>
      </div>
      <span className="self-center text-gray-300">—</span>
      <div className="relative flex-1">
        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-xs text-gray-400 font-bold">₹</span>
        <input type="number" value={maxPrice} onChange={e=>setMaxPrice(e.target.value)} placeholder="Max" min="0"
          className="w-full bg-gray-50 border border-gray-200 rounded-xl py-2.5 pl-7 pr-2 text-sm outline-none focus:ring-2 focus:ring-yellow-400/30 focus:border-yellow-500 focus:bg-white transition-all"/>
      </div>
    </div>
    <div className="flex flex-wrap gap-1.5">
      {PRICE_RANGES.map(r => (
        <button key={r.label} onClick={()=>{setMinPrice(r.min);setMaxPrice(r.max);}}
          className={`text-xs font-semibold px-2.5 py-1.5 rounded-lg transition-all duration-200
            ${minPrice===r.min&&maxPrice===r.max
              ? 'bg-yellow-100 text-yellow-800 border border-yellow-300'
              : 'border border-gray-200 text-gray-500 hover:border-red-300 hover:text-red-600 hover:bg-red-50'}`}>
          {r.label}
        </button>
      ))}
    </div>
  </div>

  {/* In-stock toggle */}
  <label className="flex items-center gap-3 cursor-pointer group">
    <div className={`relative w-10 rounded-full transition-colors duration-200 ${inStock?'bg-red-600':'bg-gray-200'}`}
      style={{height:'22px'}}>
      <input type="checkbox" className="sr-only" checked={inStock} onChange={e=>setInStock(e.target.checked)}/>
      <div className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform duration-200
        ${inStock?'translate-x-[18px]':'translate-x-0'}`}/>
    </div>
    <span className="text-sm font-semibold text-gray-700 group-hover:text-gray-900">In Stock Only</span>
  </label>

  <button onClick={()=>{reset();onClose?.();}}
    className="w-full text-sm font-semibold text-gray-400 hover:text-red-600 border border-gray-200
      hover:border-red-200 hover:bg-red-50 py-2.5 rounded-xl transition-all duration-200">
    ✕ Reset All Filters
  </button>
</div>
  );
}


export default function ProductsPage() {
  // ── SEO: update page title and meta description ───────────────
  React.useEffect(() => {
    document.title = 'Buy Crackers Online — Sivakasi Fireworks | Lakshmi Crackers';
    const meta = document.querySelector('meta[name="description"]');
    if (meta) meta.setAttribute('content', 'Shop all Diwali crackers online — sparklers, rockets, sky shots, flower pots, bomb, ground chakkar, combo packs & gift boxes. Best prices from Sivakasi. Quality assured.');
    return () => {
      document.title = 'Lakshmi Crackers 🪔 — Premium Fireworks';
    };
  }, []);

  const [searchParams, setSearchParams] = useSearchParams();
  const setParamsRef = useRef(setSearchParams);
  useEffect(() => { setParamsRef.current = setSearchParams; }, [setSearchParams]);

  const [search,   setSearch]   = useState(searchParams.get('search')    || '');
  const [category, setCategory] = useState(searchParams.get('category')  || '');
  const [minPrice, setMinPrice] = useState(searchParams.get('min_price') || '');
  const [maxPrice, setMaxPrice] = useState(searchParams.get('max_price') || '');
  const [ordering, setOrdering] = useState(searchParams.get('ordering')  || 'name');
  const [inStock,  setInStock]  = useState(false);
  const [drawerOpen,setDrawerOpen] = useState(false);

  const [products, setProducts] = useState([]);
  const [count,    setCount]    = useState(0);
  const [loading,  setLoading]  = useState(true);
  const [error,    setError]    = useState(null);

  const fetchProducts = useCallback(() => {
    const params = Object.fromEntries(
      Object.entries({ search, category, min_price:minPrice, max_price:maxPrice, ordering, in_stock:inStock?'true':'' })
        .filter(([,v]) => v)
    );
    setParamsRef.current(params, { replace:true });
    setLoading(true); setError(null);
    productsApi.list(params)
      .then(res => { const p=unwrapPage(res); setProducts(p.data); setCount(p.count); })
      .catch(err => { setError(err); setProducts([]); setCount(0); })
      .finally(() => setLoading(false));
  }, [search, category, minPrice, maxPrice, ordering, inStock]);

  useEffect(() => { const t=setTimeout(fetchProducts,350); return ()=>clearTimeout(t); }, [fetchProducts]);

  const reset = () => { setSearch(''); setCategory(''); setMinPrice(''); setMaxPrice(''); setOrdering('name'); setInStock(false); };
  const activeCount = [search,category,minPrice,maxPrice,inStock].filter(Boolean).length;



  return (
    <div className="min-h-screen" style={{background:'#fffaf0'}}>
      {/* Header */}
      <div className="bg-gradient-to-br from-red-950 via-red-800 to-red-700 relative overflow-hidden">
        <div className="absolute inset-0 deco-stripes opacity-30 pointer-events-none"/>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <p className="text-yellow-400/70 text-xs font-black uppercase tracking-widest mb-2">Sivakasi's Finest 2026</p>
          <h1 className="text-4xl font-black text-white mb-2" style={{fontFamily:'"Playfair Display",serif'}}>
            All Products
          </h1>
          <p className="text-red-200/60 text-sm">
            {count > 0 ? count : '500+'} premium products from India's fireworks capital
          </p>
        </div>
        <svg className="absolute bottom-0 left-0 right-0 w-full h-8" viewBox="0 0 1440 32" fill="none" preserveAspectRatio="none">
          <path d="M0,20 C480,40 960,0 1440,20 L1440,32 L0,32 Z" fill="#fffaf0"/>
        </svg>
      </div>

      <div className="max-w-7xl mx-auto px-2 sm:px-4 lg:px-8 py-6 sm:py-8">
        <div className="flex gap-7">
          {/* Sidebar */}
          <aside className="hidden lg:block w-64 flex-shrink-0">
            <div className="bg-white rounded-2xl border border-gray-100 p-5 sticky top-20
              shadow-[0_2px_12px_rgba(0,0,0,0.05)]">
              <div className="flex items-center justify-between mb-5">
                <p className="font-black text-gray-800">Filters</p>
                {activeCount>0 && (
                  <span className="bg-red-600 text-white text-[10px] font-black w-5 h-5 rounded-full
                    flex items-center justify-center animate-scale-in">
                    {activeCount}
                  </span>
                )}
              </div>
              <FilterPanel search={search} setSearch={setSearch} category={category} setCategory={setCategory} minPrice={minPrice} setMinPrice={setMinPrice} maxPrice={maxPrice} setMaxPrice={setMaxPrice} inStock={inStock} setInStock={setInStock} reset={reset}/>
            </div>
          </aside>

          {/* Main content */}
          <div className="flex-1 min-w-0">
            {/* Toolbar */}
            <div className="flex flex-wrap gap-3 items-center justify-between mb-5">
              <p className="text-sm text-gray-500">
                {loading ? (
                  <span className="inline-flex items-center gap-2">
                    <span className="w-3 h-3 border-2 border-red-300 border-t-red-600 rounded-full animate-spin"/>
                    Searching…
                  </span>
                ) : error ? (
                  <span className="text-red-500 font-medium">Search failed</span>
                ) : (
                  <><span className="font-black text-gray-900 text-base">{count}</span> products</>
                )}
              </p>
              <div className="flex items-center gap-2">
                <button className="lg:hidden relative flex items-center gap-2 bg-white border border-gray-200
                  px-4 py-2 rounded-xl text-sm font-semibold text-gray-600 hover:border-red-300 hover:text-red-600
                  transition-all shadow-sm" onClick={()=>setDrawerOpen(true)}>
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                      d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2a1 1 0 01-.293.707L13 13.414V19a1 1 0 01-.553.894l-4 2A1 1 0 017 21v-7.586L3.293 6.707A1 1 0 013 6V4z"/>
                  </svg>
                  Filters
                  {activeCount>0 && (
                    <span className="absolute -top-1.5 -right-1.5 bg-red-600 text-white text-[9px] font-black w-4 h-4 rounded-full flex items-center justify-center">
                      {activeCount}
                    </span>
                  )}
                </button>
                <select value={ordering} onChange={e=>setOrdering(e.target.value)}
                  className="bg-white border border-gray-200 rounded-xl py-2 pl-3 pr-8 text-sm font-semibold
                    text-gray-700 outline-none cursor-pointer hover:border-gray-300 focus:ring-2 focus:ring-yellow-400/30
                    focus:border-yellow-500 transition-all shadow-sm">
                  {SORT_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
              </div>
            </div>

            {/* Active filter chips */}
            {(category||minPrice||maxPrice||inStock) && (
              <div className="flex flex-wrap gap-2 mb-5 animate-fade-in">
                {category && (
                  <button onClick={()=>setCategory('')}
                    className="flex items-center gap-1.5 bg-red-100 text-red-700 text-xs font-bold
                      px-3 py-1.5 rounded-full hover:bg-red-200 transition-colors border border-red-200/60">
                    {CATS.find(c=>c.slug===category)?.icon} {CATS.find(c=>c.slug===category)?.name} ×
                  </button>
                )}
                {(minPrice||maxPrice) && (
                  <button onClick={()=>{setMinPrice('');setMaxPrice('');}}
                    className="flex items-center gap-1.5 bg-amber-100 text-amber-700 text-xs font-bold
                      px-3 py-1.5 rounded-full hover:bg-amber-200 transition-colors border border-amber-200/60">
                    ₹{minPrice||'0'}–₹{maxPrice||'∞'} ×
                  </button>
                )}
                {inStock && (
                  <button onClick={()=>setInStock(false)}
                    className="flex items-center gap-1.5 bg-green-100 text-green-700 text-xs font-bold
                      px-3 py-1.5 rounded-full hover:bg-green-200 transition-colors border border-green-200/60">
                    ✓ In Stock ×
                  </button>
                )}
              </div>
            )}

            {error && !loading && <ErrorBox error={error} onRetry={fetchProducts} className="mb-6"/>}

            {loading && (
              <div className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-4 gap-2 sm:gap-4 lg:gap-5">
                {[...Array(12)].map((_,i) => <ProductCardSkeleton key={i}/>)}
              </div>
            )}
            {!loading && !error && products.length===0 && (
              <EmptyState icon="🔍" title="No products found"
                message="Try widening your search or removing some filters."
                action={<button onClick={reset} className="text-red-600 font-bold hover:underline text-sm mt-2">Clear filters →</button>}/>
            )}
            {!loading && !error && products.length>0 && (
              <div className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-4 gap-2 sm:gap-4 lg:gap-5">
                {products.map((p,i) => <ProductCard key={p.id} product={p} index={i}/>)}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Mobile drawer */}
      {drawerOpen && (
        <div className="fixed inset-0 z-50 flex lg:hidden">
          <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={()=>setDrawerOpen(false)}/>
          <div className="relative bg-white w-80 max-w-[90vw] h-full overflow-y-auto shadow-2xl animate-slide-right">
            <div className="flex items-center justify-between p-5 border-b border-gray-100 sticky top-0 bg-white z-10">
              <p className="font-black text-gray-800">Filters {activeCount>0 && <span className="bg-red-600 text-white text-[10px] font-black w-5 h-5 rounded-full inline-flex items-center justify-center ml-1">{activeCount}</span>}</p>
              <button onClick={()=>setDrawerOpen(false)} className="w-8 h-8 rounded-full hover:bg-gray-100 flex items-center justify-center text-gray-400 hover:text-gray-600 transition-all text-xl">×</button>
            </div>
            <div className="p-5"><FilterPanel search={search} setSearch={setSearch} category={category} setCategory={setCategory} minPrice={minPrice} setMinPrice={setMinPrice} maxPrice={maxPrice} setMaxPrice={setMaxPrice} inStock={inStock} setInStock={setInStock} reset={reset} onClose={()=>setDrawerOpen(false)}/></div>
          </div>
        </div>
      )}
    </div>
  );
}
